import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import PropTypes from 'prop-types';

import { StyledTextField } from '../../../../../../components/Inputs';
import { LightTag } from '../../../../../../components/Tags';

import {
  AutocompleteWrapper,
  StyledAutocomplete,
  AutocompleteOption,
  AutocompleteOptionHeader,
  AutocompleteOptionClassification,
  AutocompleteOptionMacros,
  CreateOption,
  IngredientAutocompleteLabel,
} from './styles';

import texts from './texts';
import { formatMacro, macroPillText } from './utils';

const FREEFORM_ID = '__freeform__';
const MAX_OPTIONS = 50;
const POPPER_MIN_WIDTH = 480;

const autocompleteSlotProps = {
  popper: {
    placement: 'bottom-start',
    style: { width: 'auto', minWidth: POPPER_MIN_WIDTH },
    modifiers: [
      { name: 'flip', enabled: false },
      { name: 'preventOverflow', options: { altAxis: false, mainAxis: false } },
    ],
  },
};

const isCreateOption = (option) => option && option.id === FREEFORM_ID;

const IngredientAutocomplete = ({
  name,
  canonicalIngredient,
  canonicalIngredients,
  onChange,
  label,
}) => {
  const [inputValue, setInputValue] = useState(name || '');

  useEffect(() => {
    setInputValue(name || '');
  }, [name]);

  const value = canonicalIngredient || null;

  const filterOptions = useCallback((list, state) => {
    const q = (state.inputValue || '').trim().toLowerCase();
    const matched = q
      ? list.filter((opt) => opt.name.toLowerCase().includes(q))
      : list;
    const trimmed = matched.slice(0, MAX_OPTIONS);
    if (q && !matched.some((opt) => opt.name.toLowerCase() === q)) {
      trimmed.push({
        id: FREEFORM_ID,
        name: state.inputValue,
      });
    }
    return trimmed;
  }, []);

  const handleChange = useCallback((_event, newValue) => {
    if (newValue == null) {
      onChange({ name: '', id: null });
      return;
    }
    if (typeof newValue === 'string') {
      onChange({ name: newValue, id: null });
      return;
    }
    if (isCreateOption(newValue)) {
      onChange({ name: newValue.name, id: null });
      return;
    }
    onChange({
      name: newValue.name,
      id: newValue.id,
      allergenTags: newValue.allergenTags,
    });
  }, [onChange]);

  const handleInputChange = useCallback((_event, newInputValue, reason) => {
    if (reason === 'reset') {
      setInputValue(name || '');
      return;
    }
    setInputValue(newInputValue);
    if (reason === 'clear') {
      onChange({ name: '', id: null });
    }
  }, [onChange, name]);

  const handleBlur = useCallback(() => {
    if (!inputValue) return;
    if (value && value.name === inputValue) return;
    if (name === inputValue) return;
    onChange({ name: inputValue, id: null });
  }, [inputValue, value, name, onChange]);

  const getOptionLabel = useCallback((option) => {
    if (typeof option === 'string') return option;
    return option?.name || '';
  }, []);

  const renderOption = useCallback((props, option) => {
    if (isCreateOption(option)) {
      return (
        <li {...props} key={FREEFORM_ID}>
          <CreateOption>{`${texts.autocomplete.addFreeform} "${option.name}"`}</CreateOption>
        </li>
      );
    }
    return (
      <li {...props} key={option.id}>
        <AutocompleteOption>
          <AutocompleteOptionHeader>{option.name}</AutocompleteOptionHeader>
          {!!option.category && (
            <AutocompleteOptionClassification>
              {option.category}
            </AutocompleteOptionClassification>
          )}
          <AutocompleteOptionMacros>
            <LightTag>{`${formatMacro(option.caloriesPer100g)} ${texts.autocomplete.caloriesLabel}`}</LightTag>
            <LightTag>{macroPillText(texts.autocomplete.proteinLabel, option.proteinPer100g)}</LightTag>
            <LightTag>{macroPillText(texts.autocomplete.carbsLabel, option.carbsPer100g)}</LightTag>
            <LightTag>{macroPillText(texts.autocomplete.fatLabel, option.fatPer100g)}</LightTag>
          </AutocompleteOptionMacros>
        </AutocompleteOption>
      </li>
    );
  }, []);

  const isOptionEqualToValue = useCallback((option, selected) => {
    if (!option || !selected) return false;
    return option.id === selected.id;
  }, []);

  const renderInput = useCallback((params) => (
    <StyledTextField
      {...params}
      onBlur={handleBlur}
    />
  ), [handleBlur]);

  return (
    <AutocompleteWrapper>
      {!!label && <IngredientAutocompleteLabel>{label}</IngredientAutocompleteLabel>}
      <StyledAutocomplete
        freeSolo
        fullWidth
        componentsProps={autocompleteSlotProps}
        options={canonicalIngredients}
        value={value}
        inputValue={inputValue}
        onChange={handleChange}
        onInputChange={handleInputChange}
        getOptionLabel={getOptionLabel}
        filterOptions={filterOptions}
        isOptionEqualToValue={isOptionEqualToValue}
        renderOption={renderOption}
        renderInput={renderInput}
        clearOnBlur={false}
        selectOnFocus
        handleHomeEndKeys
      />
    </AutocompleteWrapper>
  );
};

IngredientAutocomplete.propTypes = {
  name: PropTypes.string,
  canonicalIngredient: PropTypes.object,
  canonicalIngredients: PropTypes.arrayOf(PropTypes.object),
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
};

IngredientAutocomplete.defaultProps = {
  name: '',
  canonicalIngredient: null,
  canonicalIngredients: [],
  label: '',
};

export default IngredientAutocomplete;
